import React from 'react';
import PropTypes from 'prop-types';
import { ErrorComponent } from '../../components/ErrorComponent/ErrorComponent';
import { ShimmerComponent } from '../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../components/Shimmer/BouncingLogo';
import PerformanceHeader from './components/PerformanceHeader';
import FilterControls from './components/FilterControls';
import TableOne from './components/TableOne';
import TableTwo from './components/TableTwo';
import TableThree from './components/TableThree';
import { useOverallPerformanceData } from './hooks/useOverallPerformanceData';
import OverTimeLineGraph from '../spendAndOpportunitiesScreen/components/OverTimeLineGraph';
import PercOverTimeLineGraph from '../spendAndOpportunitiesScreen/components/PercOverTimeLineGraph';

// Memoized graph components to prevent unnecessary re-renders
const MemoizedOverTimeLineGraph = React.memo(OverTimeLineGraph);
const MemoizedPercOverTimeLineGraph = React.memo(PercOverTimeLineGraph);

const OverallPerformance = ({ subBranchDashboard = false }) => {
  const {
    // User data
    userData,

    // Filter state
    options,
    regionOptions,
    storeId,
    regionId,
    regionBranches,

    // UI state
    spendToggleState,
    setSpendToggleState,
    showSpendToggle,
    defaultFilter,
    componentType,
    isAnyQueryLoading,
    resetRegion,
    resetStore,

    // Table data
    tableOneData,
    tableTwoData,
    tableThreeData,

    // Filter handlers
    handleStoreIdChange,
    handleRegionIdChange,
    handleResetForRegion,
    handleResetForStore,

    // Query states
    isTableOneLoading,
    isTableTwoLoading,
    isTableThreeLoading,
    tableOneError,
    tableTwoError,
    tableThreeError,

    // Table visibility conditions
    showTableThree,

    // Download filters
    branchDownloadFilters,
    localSpendFilters,
    regionDownloadFilters,
    showGraphs,
    oppsGraphProps,
    spendsGraphProps,
  } = useOverallPerformanceData({ subBranchDashboard });

  // Return error component if no stores are assigned
  if (options?.length === 0) {
    return <ErrorComponent />;
  }

  return (
    <div style={{ height: '88vh', overflow: 'auto', padding: '20px', paddingBottom: '100px' }}>
      {/* Header with title and toggle */}
      <PerformanceHeader
        showSpendToggle={showSpendToggle}
        spendToggleState={spendToggleState}
        setSpendToggleState={setSpendToggleState}
        isDisabled={isAnyQueryLoading}
        userData={userData}
        storeId={storeId}
        options={options}
        subBranchDashboard={subBranchDashboard}
      />

      {/* Filter controls section */}
      <FilterControls
        regionOptions={regionOptions}
        regionId={regionId}
        resetRegion={resetRegion}
        handleResetForRegion={handleResetForRegion}
        handleRegionIdChange={handleRegionIdChange}
        options={options}
        regionBranches={regionBranches}
        userData={userData}
        storeId={storeId}
        resetStore={resetStore}
        handleResetForStore={handleResetForStore}
        handleStoreIdChange={handleStoreIdChange}
        defaultFilter={defaultFilter}
        componentType={componentType}
        isDisabled={isAnyQueryLoading}
        branchDownloadFilters={branchDownloadFilters}
        regionDownloadFilters={regionDownloadFilters}
        localSpendFilters={localSpendFilters}
        spendToggleState={spendToggleState}
        subBranchDashboard={subBranchDashboard}
      />

      {/* Display loader if all tables are loading */}
      {isAnyQueryLoading ? (
        <ShimmerComponent
          updatedStyle={{
            width: '90%',
            height: '100%',
            backgroundSize: '2000px 800px',
            border: '0px',
            margin: 'auto',
            padding: '0 20px',
          }}
        >
          <BouncingLogo height="72vh" text="Loading..." />
        </ShimmerComponent>
      ) : (
        <div style={{ width: '90%', margin: 'auto' }}>
          {/* Table One - Period Comparison */}
          <TableOne
            data={tableOneData}
            isLoading={isTableOneLoading}
            error={tableOneError}
            subBranchDashboard={subBranchDashboard}
            showNoStoreSelected={subBranchDashboard && !storeId?.length}
          />

          {/* Table Two - YTD Comparison */}
          <TableTwo
            data={tableTwoData}
            isLoading={isTableTwoLoading}
            error={tableTwoError}
            subBranchDashboard={subBranchDashboard}
            showNoStoreSelected={subBranchDashboard && !storeId?.length}
          />

          {/* Table Three - Region Comparison */}
          {showTableThree && (
            <TableThree
              data={tableThreeData}
              isLoading={isTableThreeLoading}
              error={tableThreeError}
            />
          )}
        </div>
      )}
      <div style={{ width: '90%', margin: 'auto' }}>
        {/* Spends Graphs - After Table Two */}
        {showGraphs.base && (
          <MemoizedOverTimeLineGraph key="spends-over-time" {...spendsGraphProps} />
        )}

        {showGraphs.percentage && (
          <MemoizedPercOverTimeLineGraph key="spends-perc-over-time" {...spendsGraphProps} />
        )}

        {/* Opportunities Graphs - After Table One */}
        {showGraphs.base && <MemoizedOverTimeLineGraph key="opps-over-time" {...oppsGraphProps} />}

        {showGraphs.percentage && (
          <MemoizedPercOverTimeLineGraph key="opps-perc-over-time" {...oppsGraphProps} />
        )}
      </div>
    </div>
  );
};

OverallPerformance.propTypes = {
  subBranchDashboard: PropTypes.bool,
};

export default React.memo(OverallPerformance);
